import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface OrganizerRouteProps {
  children: ReactNode;
}

export const OrganizerRoute = ({ children }: OrganizerRouteProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!user) {
      toast({
        title: t('toasts.authRoutes.authRequired'),
        description: t('toasts.authRoutes.authRequiredDesc'),
      });
      navigate("/auth", { replace: true, state: { from: location } });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Server-side authorization: organizer flag (canonical) OR any admin role
        const [{ data: isOrg }, { data: isAdmin }] = await Promise.all([
          supabase.rpc("is_organizer", { _user_id: user.id }),
          supabase.rpc("user_is_admin", { _uid: user.id }),
        ]);
        if (cancelled) return;
        const ok = Boolean(isOrg) || Boolean(isAdmin);
        setAllowed(ok);
        if (!ok) {
          toast({
            title: t('toasts.authRoutes.organizerRequired'),
            description: t('toasts.authRoutes.organizerRequiredDesc'),
          });
          navigate("/profile", { replace: true });
        }
      } catch {
        if (!cancelled) {
          setAllowed(false);
          navigate("/profile", { replace: true });
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]);

  if (!user) return null;
  if (checking) return null;
  if (!allowed) return null;

  return <>{children}</>;
};